const LANGUAGE_CODES = {
  'en-US': ['en-us', 'en-gb', 'en-au'],
  'zh-HK': ['zh-hk', 'zh-hant-hk', 'yue', 'yue-hk', 'yue-hant-hk'],
  'zh-CN': ['zh-cn', 'zh-sg', 'zh-tw', 'zh-hans-cn', 'zh-hant-cn', 'zh-hans-sg', 'zh-hant-sg', 'zh-hans-tw', 'zh-hant-tw', 'cmn', 'cmn-cn', 'cmn-tw', 'cmn-hans-cn', 'cmn-hant-tw']
}

const PREFERRED_NAMES = {
  'en-US': [
    'microsoft aria online',
    'microsoft jenny online',
    'microsoft guy online',
    'google us english',
    'samantha',
    'ava',
    'alex',
    'daniel',
    'karen',
    'moira',
    'serena'
  ],
  'zh-HK': ['sin-ji', 'cantonese', 'hong kong', 'hiugaai', 'hiumaan', '廣東', '粤語', '粵語'],
  'zh-CN': ['ting-ting', 'mei-jia', 'yu-shu', 'mandarin', 'xiaoxiao', 'yunxi', 'huihui', 'yaoyao', '普通話', '普通话']
}

const EFFECT_VOICE = /bad news|bahh|bells|boing|bubbles|cellos|jester|organ|superstar|trinoids|whisper|wobble|zarvox/i
let activeSpeech = null

function normalizedLanguage (language) {
  return (language || '').replace(/_/g, '-').toLowerCase()
}

export function defaultSpeechRate (language) {
  return language === 'en-US' ? 0.85 : 1
}

export function getVoices () {
  return window.speechSynthesis ? window.speechSynthesis.getVoices() : []
}

export function findVoice (language, voices = getVoices()) {
  const languages = LANGUAGE_CODES[language] || [normalizedLanguage(language)]
  const compatibleVoices = voices.filter(voice => languages.includes(normalizedLanguage(voice.lang)))
  const preferredVoice = (PREFERRED_NAMES[language] || [])
    .map(name => compatibleVoices.find(voice => voice.name.toLowerCase().includes(name)))
    .find(Boolean)
  const normalVoice = compatibleVoices.find(voice => !EFFECT_VOICE.test(voice.name))

  return preferredVoice || normalVoice || compatibleVoices[0] || null
}

export function speechContentLanguage (language) {
  if (language === 'en-US') return 'en'
  if (language === 'zh-HK' || language === 'zh-CN') return 'zh-Hant'
  return null
}

function cancelActiveSpeech () {
  const previous = activeSpeech
  // Invalidate before cancel(), which can synchronously dispatch old events.
  activeSpeech = null
  if (previous) previous.finish('cancel')
  if (window.speechSynthesis) window.speechSynthesis.cancel()
}

// Both page narration and glossary playback share the browser's single queue.
export function createSpeechPlayer () {
  // Prime asynchronously loaded voice lists when the component is created.
  // Playback still re-reads the list synchronously on the user's click.
  getVoices()
  let current = null
  const ownsSpeech = () => current !== null && current === activeSpeech

  return {
    speak (text, language, options = {}) {
      // A failed language switch must still stop the previous language.
      cancelActiveSpeech()
      const synth = window.speechSynthesis
      if (!synth || !window.SpeechSynthesisUtterance) return 'vocabulary.speech_unsupported'
      if (typeof text !== 'string' || !text.trim()) return 'vocabulary.text_unavailable'

      // Fetch native voice objects at the click, not a stale/reactive copy.
      const voice = findVoice(language)
      if (!voice) return 'vocabulary.voice_unavailable'

      const utterance = new window.SpeechSynthesisUtterance(text.trim())
      const session = {
        utterance,
        finish (reason) {
          if (current !== session) return
          current = null
          if (activeSpeech === session) activeSpeech = null
          if (options.onFinish) options.onFinish(reason)
        }
      }
      current = session
      activeSpeech = session
      utterance.onstart = () => {
        if (ownsSpeech() && current === session && options.onStart) options.onStart()
      }
      utterance.onend = () => {
        if (ownsSpeech() && current === session) session.finish('end')
      }
      utterance.onerror = event => {
        if (!ownsSpeech() || current !== session) return
        session.finish('error')
        if (!['canceled', 'interrupted'].includes(event.error) && options.onError) options.onError(event)
      }

      try {
        utterance.lang = normalizedLanguage(voice.lang)
        utterance.voice = voice
        utterance.rate = options.rate || defaultSpeechRate(language)
        utterance.pitch = 1
        utterance.volume = 1
        // cancel() does not clear the global paused state.
        if (synth.paused) synth.resume()
        // Keep this synchronous inside the user's click for Safari activation.
        synth.speak(utterance)
        return null
      } catch (error) {
        cancelActiveSpeech()
        return 'vocabulary.speech_failed'
      }
    },
    stop () {
      if (ownsSpeech()) cancelActiveSpeech()
    },
    pause () {
      if (!ownsSpeech()) return false
      window.speechSynthesis.pause()
      return true
    },
    resume () {
      if (!ownsSpeech()) return false
      window.speechSynthesis.resume()
      return true
    }
  }
}
